import { useEffect, useState, useMemo } from "react";
import { InfoHint } from "../components/InfoHint";
import { FiltriCollapsible } from "../components/FiltriCollapsible";
import { ScadenzaBadge } from "../components/ScadenzaBadge";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { compressImage } from "../lib/compressImage";
import { highlightMatch } from "../lib/highlightMatch";
import { useDropZone } from "../lib/useDropZone";
import { DatePickerField } from "../components/DatePickerField";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Card } from "../components/ui/card";
import { SharedCard, getBandPreventivo } from "../components/SharedCard";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PreventivoBadge } from "../components/PreventivoBadge";
import { FileText, PlusCircle, ChevronRight, X, ChevronDown, Users, MapPin, Search, Trash2, Wrench, CheckCircle2, Camera, Image as ImageIcon, Paperclip, CalendarClock, FileCheck2, Archive, Download, Tag, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AnalisiAIPreventiviModal } from "../components/AnalisiAIPreventiviModal";

const STATI = [
  { value: "Richiesto", label: "Richiesti" },
  { value: "IncaricoAccettato", label: "Accettati" },
  { value: "Ricevuto", label: "Ricevuti" },
  { value: "Sottoscritto", label: "Sottoscritti" },
  { value: "Rifiutato", label: "Rifiutati" },
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { dateStyle: "medium" }) : "—");
const sameContatto = (a, b) => a.nome === b.nome && (a.unita || "") === (b.unita || "") && (a.telefono || "") === (b.telefono || "");

export default function Preventivi() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin";
  const [list, setList] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [q, setQ] = useState("");
  // Iter122k131 · Ricerca & export richiudibili
  const [prevFiltriOpen, setPrevFiltriOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // Iter123k159 · modale analisi AI dei preventivi
  const [showAnalisiAI, setShowAnalisiAI] = useState(false);
  const [fornitori, setFornitori] = useState([]);
  const [condomini, setCondomini] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState({ fornitore_ids: [], descrizione: "", categoria: "", condominio_id: "", ticket_id: "", data_scadenza: "", contatti_referenti: [], oggetto_ai: "" });
  const [categorie, setCategorie] = useState([]);
  const [saving, setSaving] = useState(false);
  // Iter122cq · Bozze
  const [bozze, setBozze] = useState([]);
  const [bozzaCorrente, setBozzaCorrente] = useState(null);
  // Iter122k11 · Bozze dell'editor "Conferma preventivo" (db.conferme con stato="bozza")
  const [confermeBozze, setConfermeBozze] = useState([]);
  const [contatti, setContatti] = useState([]);
  const [contattiOpen, setContattiOpen] = useState(false);
  const [filtroRuolo, setFiltroRuolo] = useState(null);
  // Iter122cq · carica bozze all'apertura
  useEffect(() => { loadBozze(); loadConfermeBozze(); /* eslint-disable-next-line */ }, []);
  // Memoria filtro per condominio (localStorage)
  useEffect(() => {
    if (!form.condominio_id) return;
    try {
      const stored = JSON.parse(localStorage.getItem("preventivi_filtro_ruolo") || "{}");
      const val = stored[form.condominio_id];
      setFiltroRuolo(val === undefined ? null : val);
    } catch { setFiltroRuolo(null); }
  }, [form.condominio_id]);
  const setAndRememberFiltro = (roles) => {
    setFiltroRuolo(roles);
    if (!form.condominio_id) return;
    try {
      const stored = JSON.parse(localStorage.getItem("preventivi_filtro_ruolo") || "{}");
      stored[form.condominio_id] = roles;
      localStorage.setItem("preventivi_filtro_ruolo", JSON.stringify(stored));
    } catch { /* ignore */ }
  };
  const contattiFiltrati = useMemo(() => {
    if (!filtroRuolo) return contatti;
    const set = new Set(filtroRuolo);
    return contatti.filter((c) => {
      const ruoli = (c.ruolo || "").split(",").map((r) => r.trim()).filter(Boolean);
      return ruoli.some((r) => set.has(r));
    });
  }, [contatti, filtroRuolo]);
  const [fornitoreSearch, setFornitoreSearch] = useState("");
  const [fornitoreOpen, setFornitoreOpen] = useState(false);
  const [allegati, setAllegati] = useState([]);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [includiEsec, setIncludiEsec] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const pf = location.state?.prefillPreventivo;
    if (pf && isAdmin) {
      setShowCreate(true);
      setForm((f) => ({ ...f, condominio_id: pf.condominio_id || "", ticket_id: pf.ticket_id || "", descrizione: pf.descrizione || "" }));
      setIncludiEsec({ ticket_id: pf.ticket_id, ticket_numero: pf.ticket_numero });
      // Carica gli allegati dell'intervento eseguito come chip visibili/rimovibili
      if (pf.ticket_id) {
        api.get(`/tickets/${pf.ticket_id}`).then((r) => {
          const t = r.data || {};
          const esec = [...(t.foto_esecuzione || []), ...(t.allegati_esecuzione || [])]
            .filter((a) => a && a.id)
            .map((a) => ({ id: a.id, name: a.original_filename || "Allegato intervento", fromEsec: true }));
          if (esec.length) setAllegati((prev) => {
            const have = new Set(prev.map((x) => x.id));
            return [...prev, ...esec.filter((e) => !have.has(e.id))];
          });
        }).catch(() => {});
      }
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (allegati.length + files.length > 5) { toast.error("Massimo 5 allegati per richiesta"); return; }
    setUploadingAtt(true);
    try {
      for (const file of files) {
        if (file.size > 20 * 1024 * 1024) { toast.error(`"${file.name}" supera i 20 MB`); continue; }
        const compressed = await compressImage(file);
        const fd = new FormData();
        fd.append("file", compressed);
        const { data } = await api.post("/uploads/documento", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAllegati((prev) => [...prev, { id: data.id, name: data.original_filename || file.name }]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Caricamento allegato non riuscito");
    } finally { setUploadingAtt(false); }
  };
  const removeAllegato = (id) => setAllegati((prev) => prev.filter((a) => a.id !== id));
  // Iter122fp15 · drag&drop file su area allegati preventivo (stesso hook usato in Ras/Impostazioni)
  const { dragProps, isDragOver } = useDropZone(uploadFiles, { disabled: uploadingAtt });

  const fornitoriSelezionati = fornitori.filter((f) => form.fornitore_ids.includes(f.id));
  const toggleFornitore = (id) => setForm((f) => ({
    ...f,
    fornitore_ids: f.fornitore_ids.includes(id) ? f.fornitore_ids.filter((x) => x !== id) : [...f.fornitore_ids, id],
  }));
  const fornitoriFiltrati = fornitori.filter((f) => {
    const q = fornitoreSearch.trim().toLowerCase();
    if (!q) return true;
    return [f.ragione_sociale, f.settore, ...(f.categorie || []), f.email, f.citta]
      .filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  const load = () => {
    api.get("/preventivi").then((r) => setList(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);
  const baseFiltrata = filtro === "__ARCHIVIO__"
    ? list.filter((p) => p.archiviato)
    : filtro === "__SCADUTI__"
      // Iter122js · Scaduti: preventivi con `in_ritardo=true` (non archiviati), stato non chiuso
      ? list.filter((p) => !p.archiviato && p.in_ritardo && !["Sottoscritto", "Rifiutato", "Chiuso"].includes(p.stato))
      : (filtro ? list.filter((p) => !p.archiviato && p.stato_key === filtro) : list.filter((p) => !p.archiviato));
  const qn = q.trim().toLowerCase();
  const listaFiltrata = qn
    ? baseFiltrata.filter((p) => [p.numero, p.condominio_nome, p.fornitore_nome, p.descrizione].some((v) => (v || "").toLowerCase().includes(qn)))
    : baseFiltrata;

  const esportaCsv = () => {
    const rows = [["Numero", "Stato", "Condominio", "Fornitore", "Importo", "Scadenza", "Archiviato", "Manutenzione"]];
    listaFiltrata.forEach((p) => rows.push([
      p.numero, p.stato_display || p.stato, p.condominio_nome || "", p.fornitore_nome || "",
      p.importo || "", p.data_scadenza || "", p.archiviato ? "Sì" : "No", p.ticket_numero || "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `preventivi_${filtro === "__ARCHIVIO__" ? "archivio" : "elenco"}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const esportaPdf = async () => {
    try {
      const params = {};
      if (filtro === "__ARCHIVIO__") params.archivio = true;
      else if (filtro) params.stato_key = filtro;
      if (qn) params.q = qn;
      const r = await api.get("/preventivi/export/pdf", { params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `preventivi_${filtro === "__ARCHIVIO__" ? "archivio" : "elenco"}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Export PDF non riuscito"); }
  };

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/fornitori", { params: { limit: 1000 } }).then((r) => setFornitori(r.data.fornitori)).catch(() => {});
    api.get("/condomini").then((r) => setCondomini(r.data)).catch(() => {});
    api.get("/tickets").then((r) => setTickets(r.data)).catch(() => {});
    api.get("/meta").then((r) => setCategorie(r.data.categorie || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!form.condominio_id) { setContatti([]); return; }
    api.get(`/condomini/${form.condominio_id}/contatti`).then((r) => setContatti(r.data || [])).catch(() => setContatti([]));
  }, [form.condominio_id]);

  const toggleContatto = (c) => setForm((f) => {
    const exists = f.contatti_referenti.some((x) => sameContatto(x, c));
    const list = exists ? f.contatti_referenti.filter((x) => !sameContatto(x, c)) : [...f.contatti_referenti, c];
    return { ...f, contatti_referenti: list };
  });

  const removePreventivo = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare definitivamente la richiesta di preventivo ${p.numero}?`)) return;
    try {
      await api.delete(`/preventivi/${p.id}`);
      toast.success("Richiesta eliminata");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const create = async () => {
    if (!form.fornitore_ids.length) { toast.error("Seleziona almeno un fornitore"); return; }
    if (!form.descrizione.trim()) { toast.error("Inserisci la descrizione della richiesta"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (k !== "contatti_referenti" && k !== "fornitore_ids" && !payload[k]) delete payload[k]; });
      payload.allegati_ids = allegati.filter((a) => !a.fromEsec).map((a) => a.id);
      const esecIds = allegati.filter((a) => a.fromEsec).map((a) => a.id);
      if (form.ticket_id && esecIds.length) payload.allegati_esecuzione_ids = esecIds;
      const r = await api.post("/preventivi", payload);
      const n = r.data?.count || 1;
      toast.success(n > 1 ? `${n} richieste di preventivo inviate ai fornitori` : "Richiesta di preventivo inviata al fornitore");
      // Elimina la bozza se ne stavamo caricando una
      if (bozzaCorrente) {
        try { await api.delete(`/preventivi/bozze/${bozzaCorrente}`); } catch (_e) { /* ignora */ }
        setBozzaCorrente(null);
      }
      setShowCreate(false);
      setForm({ fornitore_ids: [], descrizione: "", categoria: "", condominio_id: "", ticket_id: "", data_scadenza: "", contatti_referenti: [], oggetto_ai: "" });
      setAllegati([]);
      setIncludiEsec(null);
      load();
      loadBozze();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  // Iter122cq · Bozze
  const loadBozze = () => api.get("/preventivi/bozze").then((r) => setBozze(r.data || [])).catch(() => {});
  // Iter122k11 · Bozze editor Conferma preventivo (stato="bozza", contesto preventivo — no sinistro/manutenzione/appalto)
  // Iter122k45 · escludi conferme già linkate a un preventivo (auto_preventivo_id o preventivo_id):
  // se il preventivo è stato generato/collegato, la pratica NON è più una bozza — evita duplicati.
  const loadConfermeBozze = () => api.get("/conferme").then((r) => {
    const all = (r.data?.conferme || []);
    setConfermeBozze(all.filter((c) => c.stato === "bozza" &&
      (!c.contesto || c.contesto === "preventivo") &&
      !c.sinistro_id && !c.ticket_id && !c.appalto_id &&
      !c.preventivo_id && !c.auto_preventivo_id));
  }).catch(() => {});
  const eliminaConfermaBozza = async (c) => {
    if (!window.confirm(`Eliminare la bozza "${c.titolo || c.numero}"?`)) return;
    try { await api.delete(`/conferme/${c.id}`); toast.success("Bozza eliminata"); loadConfermeBozze(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const salvaBozza = async () => {
    // Bozza salvabile con almeno UN campo compilato (fornitore NON obbligatorio)
    if (!form.descrizione.trim() && !form.fornitore_ids.length && !form.categoria && !form.condominio_id) {
      toast.error("Compila almeno un campo (descrizione, fornitore, categoria o condominio) per salvare la bozza"); return;
    }
    // Iter122fp6 · Titolo automatico: `window.prompt` è bloccato in molte PWA/WebView installate
    // e ritornava `null` silenziosamente facendo apparire il salvataggio "fallito" all'utente.
    // Ora costruiamo il titolo dalla descrizione (o timestamp) senza dialog bloccante.
    const stamp = `${new Date().toLocaleDateString("it-IT")} ${new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
    const titolo = (form.descrizione || "").trim().slice(0, 60) || `Bozza ${stamp}`;
    setSaving(true);
    try {
      const payload = { form, allegati: allegati.map((a) => ({ id: a.id, filename: a.filename, fromEsec: !!a.fromEsec })), includiEsec };
      await api.post("/preventivi/bozze", { titolo, payload });
      toast.success(`Bozza "${titolo}" salvata`);
      loadBozze();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore salvataggio bozza"); }
    setSaving(false);
  };
  const caricaBozza = (b) => {
    if (!window.confirm(`Caricare la bozza "${b.titolo}"? Sovrascriverà il form corrente.`)) return;
    setForm(b.payload?.form || form);
    setAllegati(b.payload?.allegati || []);
    setIncludiEsec(b.payload?.includiEsec ?? null);
    setBozzaCorrente(b.id);
    setShowCreate(true);
    setFiltro("");  // Iter122fp11 · reset del tab: quando la pratica è aperta non serve la vista Bozze
    toast.success(`Bozza "${b.titolo}" caricata`);
  };
  const eliminaBozza = async (b) => {
    if (!window.confirm(`Eliminare la bozza "${b.titolo}"?`)) return;
    try { await api.delete(`/preventivi/bozze/${b.id}`); toast.success("Bozza eliminata"); loadBozze(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  return (
    <div className="max-w-6xl" data-testid="preventivi-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <FileText className="w-9 h-9 text-brand" /> Preventivi
          </h1>
          <InfoHint section="preventivi" />
        </div>
        {isAdmin && (
          <div className="flex flex-nowrap items-stretch gap-1.5 sm:gap-2 w-full sm:w-auto">
            {(() => {
              // Iter123k159 · conta preventivi confrontabili raggruppati (≥2 per gruppo).
              // Iter123k200 · "Oggetto / riferimento per AI" ha priorità sul ticket_id e
              // sulla descrizione normalizzata: è il collante esplicito scelto dall'admin
              // per legare richieste identiche inviate a più fornitori.
              const STATI_CON_OFFERTA = new Set(["Inviato", "IncaricoAccettato", "Accettato", "Ricevuto", "Sottoscritto"]);
              const normDesc = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().slice(0, 120);
              const groups = new Map();
              (list || []).forEach((p) => {
                if (!STATI_CON_OFFERTA.has(p.stato)) return;
                const oggetto = normDesc(p.oggetto_ai);
                const k = oggetto ? `ogg::${oggetto}` : (p.ticket_id || `desc::${normDesc(p.descrizione)}`);
                if (!k || k === "desc::" || k === "ogg::") return;
                groups.set(k, (groups.get(k) || 0) + 1);
              });
              const numConfrontabili = Array.from(groups.values()).filter((n) => n >= 2).length;
              if (numConfrontabili === 0) return null;
              // Iter123k167 · restano in linea anche su mobile: font e padding ridotti
              return (
                <Button data-testid="preventivi-analisi-ai-button" variant="outline" onClick={() => setShowAnalisiAI(true)}
                  className="h-11 sm:h-12 px-1.5 sm:px-4 text-[11px] sm:text-base font-bold flex-1 sm:flex-none min-w-0 justify-center border-brand/40 text-brand hover:bg-[#FBEEF3]">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 shrink-0" />
                  <span className="truncate">AI</span>
                  <span className="ml-1 sm:ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] sm:text-xs bg-brand text-white font-bold shrink-0">{numConfrontabili}</span>
                </Button>
              );
            })()}
            <Button data-testid="conferma-preventivo-button" variant="outline" onClick={() => navigate("/conferma-preventivo")}
              className="h-11 sm:h-12 px-1.5 sm:px-4 text-[11px] sm:text-base font-bold flex-1 sm:flex-none min-w-0 justify-center text-brand border-brand/40 hover:bg-[#FBEEF3]">
              <FileCheck2 className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 shrink-0" />
              <span className="truncate">Conferma</span>
            </Button>
            <Button data-testid="new-preventivo-button" onClick={() => setShowCreate((v) => !v)}
              className="h-11 sm:h-12 px-1.5 sm:px-4 text-[11px] sm:text-base font-bold flex-1 sm:flex-none min-w-0 justify-center bg-brand hover:bg-brand-dark">
              <PlusCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 shrink-0" />
              <span className="truncate">Nuova</span>
            </Button>
          </div>
        )}
      </div>

      {isAdmin && showCreate && (
        <Card className="p-4 border-2 mb-4 bg-[#FBEEF3]/40" data-testid="create-preventivo-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold">Richiedi un preventivo a un fornitore</h3>
            <Button variant="ghost" size="icon" onClick={() => { setShowCreate(false); setIncludiEsec(null); }}><X className="w-5 h-5" /></Button>
          </div>
          {includiEsec && (
            <div data-testid="includi-esec-note" className="mb-4 flex items-start gap-2 rounded-lg border-2 border-teal-200 bg-teal-50/60 p-3 text-sm text-teal-800">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <span>Sono stati pre-caricati gli <b>allegati dell'intervento eseguito</b> della manutenzione {includiEsec.ticket_numero ? <b>n. {includiEsec.ticket_numero}</b> : "collegata"} (vedi sotto): puoi <b>visualizzarli</b>, <b>rimuoverli</b> con la X o <b>aggiungerne altri</b>. Seleziona il fornitore e invia la richiesta.</span>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="min-w-0">
              <Label className="text-base font-bold">Fornitori * <span className="font-normal text-slate-500">(puoi selezionarne più di uno)</span></Label>
              <button type="button" data-testid="preventivo-fornitore-toggle"
                onClick={() => setFornitoreOpen((o) => !o)}
                className="h-12 w-full flex items-center justify-between border-2 border-slate-300 rounded-lg px-3 bg-white text-base mt-1 text-left">
                <span className={fornitoriSelezionati.length ? "text-slate-800 font-semibold truncate" : "text-slate-400"}>
                  {fornitoriSelezionati.length === 0 ? "Cerca e seleziona fornitori..."
                    : fornitoriSelezionati.length === 1 ? fornitoriSelezionati[0].ragione_sociale
                    : `${fornitoriSelezionati.length} fornitori selezionati`}
                </span>
                <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${fornitoreOpen ? "rotate-180" : ""}`} />
              </button>
              {fornitoriSelezionati.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="preventivo-fornitori-chips">
                  {fornitoriSelezionati.map((f) => (
                    <span key={f.id} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-brand text-white text-sm font-semibold">
                      <span className="max-w-[160px] truncate">{f.ragione_sociale}</span>
                      <button type="button" data-testid={`preventivo-fornitore-remove-${f.id}`} onClick={() => toggleFornitore(f.id)} className="hover:text-red-200"><X className="w-4 h-4" /></button>
                    </span>
                  ))}
                </div>
              )}
              {fornitoreOpen && (
                <div className="mt-2 w-full border-2 border-slate-200 rounded-lg bg-white overflow-hidden">
                  <div className="p-2 border-b bg-slate-50">
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <Input data-testid="preventivo-fornitore-search" autoFocus value={fornitoreSearch}
                        onChange={(e) => setFornitoreSearch(e.target.value)}
                        placeholder="Cerca per nome, settore, città..." className="h-10 pl-9 w-full" />
                    </div>
                  </div>
                  <div data-testid="preventivo-fornitore-list" className="max-h-60 overflow-auto divide-y">
                    {fornitoriFiltrati.length === 0 ? (
                      <div className="p-3 text-base text-slate-500">Nessun fornitore trovato.</div>
                    ) : (
                      fornitoriFiltrati.slice(0, 100).map((f) => {
                        const sel = form.fornitore_ids.includes(f.id);
                        return (
                        <button type="button" key={f.id} data-testid={`preventivo-fornitore-opt-${f.id}`}
                          onClick={() => toggleFornitore(f.id)}
                          className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center justify-between gap-2 ${sel ? "bg-[#FBEEF3]" : ""}`}>
                          <span className="min-w-0">
                            <div className="font-semibold text-slate-800 truncate">{highlightMatch(f.ragione_sociale, fornitoreSearch)}</div>
                            <div className="text-sm text-slate-500 truncate">
                              {highlightMatch([f.settore || (f.categorie || [])[0], f.citta].filter(Boolean).join(" · ") || "—", fornitoreSearch)}
                            </div>
                          </span>
                          {sel && <CheckCircle2 className="w-5 h-5 text-brand shrink-0" />}
                        </button>
                        );
                      })
                    )}
                  </div>
                  <div className="p-2 border-t bg-slate-50 text-right">
                    <Button type="button" size="sm" variant="outline" data-testid="preventivo-fornitore-done" onClick={() => { setFornitoreOpen(false); setFornitoreSearch(""); }}>Fatto</Button>
                  </div>
                </div>
              )}
            </div>
            <div className="min-w-0">
              <Label className="text-base font-bold">Condominio (opzionale)</Label>
              <select data-testid="preventivo-condominio" value={form.condominio_id}
                onChange={(e) => setForm({ ...form, condominio_id: e.target.value })}
                className="h-12 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-base mt-1">
                <option value="">—</option>
                {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div className="min-w-0">
              <Label className="text-base font-bold">Categoria (opzionale)</Label>
              <select data-testid="preventivo-categoria" value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                className="h-12 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-base mt-1">
                <option value="">— Seleziona categoria —</option>
                {categorie.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-base font-bold">Manutenzione collegata (opzionale)</Label>
              <select data-testid="preventivo-ticket" value={form.ticket_id}
                onChange={(e) => setForm({ ...form, ticket_id: e.target.value })}
                className="h-12 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-base mt-1">
                <option value="">—</option>
                {tickets.map((t) => <option key={t.id} value={t.id}>{t.numero} · {t.titolo}</option>)}
              </select>
            </div>
            <div className="min-w-0">
              <Label className="text-base font-bold">Scadenza risposta (opzionale)</Label>
              <div className="mt-1">
                <DatePickerField testid="preventivo-scadenza" value={form.data_scadenza}
                  onChange={(v) => setForm({ ...form, data_scadenza: v })} disablePast className="h-12" />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-base font-bold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand" />
              Oggetto / Riferimento per AI
              <span className="text-xs font-normal text-slate-500">(opzionale, consigliato con più fornitori)</span>
            </Label>
            <p className="text-sm text-slate-500 mb-1">
              Breve etichetta che identifica la stessa richiesta inviata a più fornitori
              (es. <i>&quot;Rifacimento lastrico solare 2026&quot;</i>). L&apos;AI la userà per raggruppare,
              confrontare e valutare automaticamente le offerte comparabili.
            </p>
            <Input data-testid="preventivo-oggetto-ai" value={form.oggetto_ai}
              onChange={(e) => setForm({ ...form, oggetto_ai: e.target.value.slice(0, 160) })}
              placeholder="Es. Rifacimento lastrico solare 2026" className="h-12 text-base" maxLength={160} />
          </div>

          <div className="mt-4">
            <Label className="text-base font-bold">Descrizione della richiesta *</Label>
            <Textarea data-testid="preventivo-descrizione" value={form.descrizione}
              onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
              placeholder="Descrivi i lavori per cui richiedi il preventivo..." className="min-h-[90px] text-lg mt-1" />
          </div>

          <div className="mt-4">
            <Label className="text-base font-bold">Allegati alla richiesta <span className="font-normal text-slate-500">(foto, PDF, Word — max 5, 20 MB l&apos;uno)</span></Label>
            <p className="text-sm text-slate-500 mb-2">Saranno inviati al fornitore via email, nel link e nella scheda del preventivo. <b>Puoi anche trascinare i file qui sotto</b>.</p>
            {/* Iter122fp15 · drop zone drag&drop: bordo tratteggiato che si evidenzia quando trascini sopra */}
            <div
              {...dragProps}
              data-testid="preventivo-allegati-dropzone"
              className={`rounded-lg border-2 border-dashed transition-all ${isDragOver ? "border-brand bg-[#FBEEF3]/60 ring-2 ring-brand/30" : "border-slate-300 bg-slate-50/40"} p-3`}
            >
              <div className="flex flex-wrap gap-2">
                <label data-testid="preventivo-att-camera" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border-2 border-slate-300 bg-white cursor-pointer text-sm font-semibold hover:bg-slate-50">
                  <Camera className="w-4 h-4" /> Scatta foto
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
                </label>
                <label data-testid="preventivo-att-gallery" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border-2 border-slate-300 bg-white cursor-pointer text-sm font-semibold hover:bg-slate-50">
                  <ImageIcon className="w-4 h-4" /> Da galleria
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
                </label>
                <label data-testid="preventivo-att-file" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border-2 border-slate-300 bg-white cursor-pointer text-sm font-semibold hover:bg-slate-50">
                  <Paperclip className="w-4 h-4" /> File dal PC
                  <input type="file" accept=".pdf,.doc,.docx,image/*" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
                </label>
                {uploadingAtt && <span className="text-sm text-slate-500 self-center">Caricamento...</span>}
                {!uploadingAtt && (
                  <span className={`text-xs italic self-center ${isDragOver ? "text-brand font-semibold" : "text-slate-400"}`}>
                    {isDragOver ? "Rilascia qui i file" : "…oppure trascina qui i file"}
                  </span>
                )}
              </div>
            </div>
            {allegati.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="preventivo-allegati-list">
                {allegati.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
                    <button type="button" onClick={() => openSecureFile(`/files/${a.id}`)} data-testid={`view-allegato-${a.id}`}
                      className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-brand hover:underline truncate">
                      <Paperclip className="w-4 h-4 shrink-0" /> <span className="truncate">{a.name}</span>
                      {a.fromEsec && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 shrink-0">intervento</span>}
                    </button>
                    <button type="button" onClick={() => removeAllegato(a.id)} className="text-red-600 hover:text-red-800 shrink-0" data-testid={`remove-allegato-${a.id}`}><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>


          <div className="mt-4">
            <Label className="text-base font-bold">Referente per i contatti (condòmino)</Label>
            <p className="text-sm text-slate-500 mb-1">Seleziona uno o più condòmini: nome e telefono saranno trasmessi al fornitore per concordare sopralluoghi. {!form.condominio_id && <span className="text-amber-700 font-semibold">Seleziona prima un condominio.</span>}</p>
            <button type="button" data-testid="preventivo-referenti-toggle" disabled={!form.condominio_id}
              onClick={() => setContattiOpen((o) => !o)}
              className="w-full h-12 flex items-center justify-between px-4 rounded-lg border-2 border-slate-300 bg-white text-base text-left disabled:opacity-50">
              <span className={form.contatti_referenti.length ? "text-slate-800 font-semibold" : "text-slate-400"}>
                {form.contatti_referenti.length ? `${form.contatti_referenti.length} referente/i selezionati` : "Seleziona i condòmini..."}
              </span>
              <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${contattiOpen ? "rotate-180" : ""}`} />
            </button>
            {contattiOpen && form.condominio_id && (
              <div data-testid="preventivo-referenti-list" className="mt-2 border-2 border-slate-200 rounded-lg max-h-56 overflow-auto">
                <div className="sticky top-0 bg-slate-50 border-b p-2 flex flex-wrap gap-1.5" data-testid="preventivo-referenti-filter">
                  {[
                    { key: "tutti", label: "Tutti", roles: null },
                    { key: "proprietari", label: "Solo Proprietari", roles: ["Proprietario", "Comproprietario"] },
                    { key: "inquilini", label: "Solo Inquilini", roles: ["Inquilino/Conduttore"] },
                  ].map((f) => (
                    <button key={f.key} type="button" data-testid={`prev-filter-${f.key}`}
                      onClick={() => setAndRememberFiltro(f.roles)}
                      className={`h-7 px-2.5 rounded-full text-xs font-semibold border-2 transition-colors ${
                        (filtroRuolo === f.roles || (filtroRuolo === null && f.roles === null))
                          ? "bg-brand text-white border-brand"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                      }`}>{f.label}</button>
                  ))}
                </div>
                <div className="divide-y">
                {contattiFiltrati.length === 0 ? (
                  <div className="p-3 text-base text-slate-500">Nessun condòmino trovato con questo filtro.</div>
                ) : (
                  contattiFiltrati.map((c, i) => {
                    const checked = form.contatti_referenti.some((x) => sameContatto(x, c));
                    return (
                      <label key={i} data-testid={`preventivo-referente-${i}`} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" checked={checked} onChange={() => toggleContatto(c)} className="w-5 h-5 accent-brand shrink-0" />
                        <div className="min-w-0">
                          <div className="font-semibold flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            {c.nome}
                            {c.unita && <span className="text-sm text-slate-500">int. {c.unita}</span>}
                            {c.ruolo && <span className="text-xs px-2 py-0.5 rounded-full bg-[#FBEEF3] text-brand">{c.ruolo}</span>}
                          </div>
                          <div className="text-sm text-slate-500">{c.telefono || "Nessun telefono in anagrafica"}</div>
                        </div>
                      </label>
                    );
                  })
                )}
                </div>
              </div>
            )}
            {form.contatti_referenti.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.contatti_referenti.map((c, i) => (
                  <span key={i} data-testid={`preventivo-referente-chip-${i}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FBEEF3] text-brand text-sm font-semibold">
                    <Users className="w-3.5 h-3.5" /> {c.nome}{c.telefono ? ` · ${c.telefono}` : ""}
                    <button type="button" onClick={() => toggleContatto(c)} aria-label="Rimuovi"><X className="w-3.5 h-3.5" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            <Button data-testid="submit-preventivo" disabled={saving || !form.fornitore_ids.length || !form.descrizione.trim()}
              className="h-12 bg-brand hover:bg-brand-dark flex-1 min-w-[200px]" onClick={create}>
              {saving ? "Invio..." : (form.fornitore_ids.length > 1 ? `Invia ${form.fornitore_ids.length} richieste` : "Invia richiesta")}
            </Button>
            <Button type="button" variant="outline" data-testid="salva-bozza-preventivo" disabled={saving}
                    className="h-12 border-2 border-brand text-brand hover:bg-[#FBEEF3]" onClick={salvaBozza}>
              💾 Salva bozza
            </Button>
          </div>
        </Card>
      )}

      {/* Iter122cq · Selettore bozze salvate (pannello di scorciatoia) — nascosto quando il tab Bozze è già attivo */}
      {bozze.length > 0 && !showCreate && filtro !== "__BOZZE__" && (
        <Card className="p-3 mb-4 border-2 border-brand/30 bg-[#FBEEF3]/40" data-testid="bozze-preventivi">
          <div className="text-xs font-semibold uppercase text-brand mb-2">Bozze salvate ({bozze.length})</div>
          <div className="flex flex-wrap gap-2">
            {bozze.slice(0, 10).map((b) => (
              <div key={b.id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-brand/30 text-xs">
                <button type="button" onClick={() => caricaBozza(b)} className="font-semibold text-slate-800 hover:text-brand"
                        data-testid={`bozza-carica-${b.id}`}>
                  {b.titolo}
                </button>
                <span className="text-slate-400">{new Date(b.updated_at).toLocaleDateString("it-IT")}</span>
                <button type="button" onClick={() => eliminaBozza(b)} aria-label="Elimina" data-testid={`bozza-elimina-${b.id}`}>
                  <X className="w-3.5 h-3.5 text-slate-400 hover:text-red-600" />
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Iter122fp11 · Quando la pratica è aperta (showCreate) nascondo la barra dei tab,
          la toolbar di ricerca e la lista sotto: l'utente si concentra sul form senza rumore */}
      {!showCreate && (<>
      <div className="flex flex-wrap gap-1.5 mb-4">
        <button data-testid="filtro-tutti" onClick={() => setFiltro("")}
          className={`px-2.5 sm:px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold border transition-colors ${filtro === "" ? "bg-brand text-white border-brand" : "bg-white border-slate-300 text-slate-600"}`}>
          Tutti
        </button>
        {STATI.map(({ value, label }) => (
          <button key={value} data-testid={`filtro-${value}`} onClick={() => setFiltro(value)}
            className={`px-2.5 sm:px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold border transition-colors ${filtro === value ? "bg-brand text-white border-brand" : "bg-white border-slate-300 text-slate-600"}`}>
            {label}
          </button>
        ))}
        <button data-testid="filtro-archivio" onClick={() => setFiltro("__ARCHIVIO__")}
          className={`px-2.5 sm:px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold border transition-colors inline-flex items-center gap-1.5 ${filtro === "__ARCHIVIO__" ? "bg-slate-700 text-white border-slate-700" : "bg-white border-slate-300 text-slate-600"}`}>
          <Archive className="w-3.5 h-3.5" /> Archivio
        </button>
        {/* Iter122fp6 · Tab "Bozze" affianco agli altri filtri stato: raccoglie le bozze
            personali dell'utente (mai inviate al fornitore). Contatore inline. */}
        {isAdmin && (
          <button data-testid="filtro-bozze" onClick={() => setFiltro("__BOZZE__")}
            className={`px-2.5 sm:px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold border transition-colors inline-flex items-center gap-1.5 ${filtro === "__BOZZE__" ? "bg-brand text-white border-brand" : "bg-white border-slate-300 text-slate-600"}`}>
            <FileText className="w-3.5 h-3.5" /> Bozze{(bozze.length + confermeBozze.length) > 0 ? ` (${bozze.length + confermeBozze.length})` : ""}
          </button>
        )}
        {/* Iter122js · Filtro rapido "Scaduti" (rosso) — preventivi in ritardo non ancora chiusi */}
        {(() => {
          const scadutiCount = list.filter((p) => !p.archiviato && p.in_ritardo && !["Sottoscritto", "Rifiutato", "Chiuso"].includes(p.stato)).length;
          return (
            <button data-testid="filtro-scaduti" onClick={() => setFiltro("__SCADUTI__")}
              className={`px-2.5 sm:px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold border-2 transition-colors inline-flex items-center gap-1.5 ${filtro === "__SCADUTI__" ? "bg-red-600 text-white border-red-600" : "bg-white border-red-500 text-red-700 hover:bg-red-50"}`}>
              <CalendarClock className="w-3.5 h-3.5" /> Scaduti{scadutiCount > 0 ? ` (${scadutiCount})` : ""}
            </button>
          );
        })()}
      </div>

      {/* Iter122k131 · Barra ricerca & export richiudibile a tendina */}
      <FiltriCollapsible
        open={prevFiltriOpen} setOpen={setPrevFiltriOpen}
        count={q ? 1 : 0}
        onReset={() => setQ("")}
        testidPrefix="preventivi"
        right={<>
          <button data-testid="preventivi-export" onClick={esportaCsv} disabled={listaFiltrata.length === 0}
            className="h-9 px-3 rounded-lg border-2 border-slate-300 bg-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <button data-testid="preventivi-export-pdf" onClick={esportaPdf} disabled={listaFiltrata.length === 0}
            className="h-9 px-3 rounded-lg border-2 border-slate-300 bg-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
            <FileText className="w-3.5 h-3.5" /> PDF
          </button>
        </>}>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="preventivi-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per condominio, fornitore, numero…"
            className="w-full h-10 pl-9 pr-3 rounded-lg border-2 border-slate-300 bg-white text-sm focus:border-brand outline-none" />
        </div>
      </FiltriCollapsible>

      <div className="mb-3" />

      <div className="space-y-2">
        {/* Iter122fp6 · Vista "Bozze" quando il tab è attivo — cardable, click carica la bozza, X elimina
            Iter122fp11 · Colori neutri (bianco/slate) — abbandonato il brand bordeaux per evitare rumore visivo
            Iter122k11 · Include anche le bozze dell'editor "Conferma preventivo" (db.conferme con stato="bozza") */}
        {filtro === "__BOZZE__" && (
          (bozze.length === 0 && confermeBozze.length === 0) ? (
            <Card className="p-5 text-center text-slate-500 text-lg border-2" data-testid="bozze-empty">
              Nessuna bozza salvata. Compila una nuova richiesta e clicca &quot;Salva bozza&quot;, oppure apri l&apos;editor &quot;Conferma preventivo&quot;.
            </Card>
          ) : (
          <>
            {confermeBozze.map((c) => (
              <SharedCard key={`conf-${c.id}`} band="bg-brand/40" data-testid={`conferma-bozza-row-${c.id}`}
                onClick={() => navigate(`/conferma-preventivo/${c.id}`)}
                className="p-3 border-2 border-brand/30 bg-white cursor-pointer hover:border-brand transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand/10 text-brand border border-brand/30 text-xs font-bold uppercase">
                        <FileCheck2 className="w-3 h-3" /> Bozza · Conferma preventivo
                      </span>
                      <span className="font-mono text-xs font-bold text-slate-500">{c.numero}</span>
                      <span className="font-semibold text-base text-slate-800 truncate max-w-[420px]" data-testid={`conferma-bozza-titolo-${c.id}`}>
                        {c.titolo || "Conferma preventivo"}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600 mt-1 truncate">
                      {[c.condominio_nome, c.fornitore_nome || c.fornitore_email].filter(Boolean).join(" · ") || <span className="italic text-slate-400">Condominio/fornitore non selezionati</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Salvata il {new Date(c.updated_at || c.created_at).toLocaleDateString("it-IT")} {new Date(c.updated_at || c.created_at).toLocaleTimeString("it-IT", {hour: "2-digit", minute: "2-digit"})}
                      {c.categoria ? ` · ${c.categoria}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <Button data-testid={`conferma-bozza-delete-${c.id}`} variant="ghost" size="icon"
                      title="Elimina bozza" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); eliminaConfermaBozza(c); }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </SharedCard>
            ))}
            {bozze.map((b) => {
            const numFornitori = (b.payload?.form?.fornitore_ids || []).length;
            const descr = (b.payload?.form?.descrizione || "").trim();
            const cond = (b.payload?.form?.condominio_id || "").trim();
            return (
              <SharedCard key={b.id} band="bg-slate-400" data-testid={`bozza-row-${b.id}`}
                onClick={() => caricaBozza(b)}
                className="p-3 border-2 border-slate-200 bg-white cursor-pointer hover:border-slate-400 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-xs font-bold uppercase">
                        <FileText className="w-3 h-3" /> Bozza
                      </span>
                      <span className="font-semibold text-base text-slate-800 truncate max-w-[520px]" data-testid={`bozza-titolo-${b.id}`}>
                        {b.titolo}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600 mt-1 truncate">
                      {descr || <span className="italic text-slate-400">Nessuna descrizione</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-2">
                      <span>Salvata il {new Date(b.updated_at).toLocaleDateString("it-IT")} {new Date(b.updated_at).toLocaleTimeString("it-IT", {hour: "2-digit", minute: "2-digit"})}</span>
                      <span>·</span>
                      <span>{numFornitori === 0 ? "Nessun fornitore" : numFornitori === 1 ? "1 fornitore" : `${numFornitori} fornitori`}</span>
                      {cond && <><span>·</span><span>Condominio selezionato</span></>}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <Button data-testid={`bozza-delete-${b.id}`} variant="ghost" size="icon"
                      title="Elimina bozza" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); eliminaBozza(b); }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </SharedCard>
            );
          })}
          </>
          )
        )}
        {filtro !== "__BOZZE__" && listaFiltrata.length === 0 && (
          <Card className="p-5 text-center text-slate-500 text-lg border-2">Nessun preventivo trovato.</Card>
        )}
        {filtro !== "__BOZZE__" && listaFiltrata.map((p) => {
          const bordoStato = getBandPreventivo(p);
          return (
          <SharedCard key={p.id} band={bordoStato} data-testid={`preventivo-row-${p.numero}`} onClick={() => navigate(`/preventivi/${p.id}`)}
            className="p-3 border-2 cursor-pointer hover:border-[#C98AA0] transition-colors">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold text-slate-500">{highlightMatch(p.numero, q)}</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-base text-slate-800" data-testid={`preventivo-fornitore-${p.numero}`}>
                    <Wrench className="w-4 h-4 text-brand shrink-0" /> <span className="break-words">{highlightMatch(p.fornitore_nome, q)}</span>
                  </span>
                  {p.messaggi_non_letti > 0 && (
                    <span data-testid={`preventivo-msg-badge-${p.numero}`} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-600 text-white">
                      <MessageCircle className="w-3 h-3" /> {p.messaggi_non_letti}
                    </span>
                  )}
                  {/* Iter122y-b · badge "Nuova richiesta / Offerta ricevuta" che concorre al contatore della sidebar */}
                  {(() => {
                    const isFornPending = !isAdmin && p.stato === "Richiesto" && !p.incarico_accettato_at && !p.archiviato && !p.visto_fornitore_at;
                    const isAdminPending = isAdmin && p.stato === "Inviato" && !p.approvato_at && !p.archiviato && !p.visto_studio_at;
                    if (!isFornPending && !isAdminPending) return null;
                    return (
                      <span data-testid={`preventivo-nuova-badge-${p.numero}`} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-600 text-white animate-pulse">
                        {isFornPending ? "Nuova richiesta" : "Offerta ricevuta"}
                      </span>
                    );
                  })()}
                </div>
                <div className="text-sm text-slate-600 mt-0.5 truncate">{p.condominio_nome ? <>{highlightMatch(p.condominio_nome, q)}{" · "}</> : ""}{highlightMatch(p.descrizione, q)}</div>
                <div className="text-sm text-slate-400 mt-0.5">Richiesto il {fmt(p.created_at)}{p.importo ? ` · ${p.importo}` : ""}</div>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                {isAdmin && (
                  <Button data-testid={`preventivo-delete-${p.numero}`} variant="ghost" size="icon"
                    title="Elimina richiesta" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={(e) => removePreventivo(e, p)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-2 pt-2 border-t border-slate-100">
              <PreventivoBadge p={p} size="sm" />
              {p.categoria && (
                <span data-testid={`preventivo-categoria-${p.numero}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border bg-brand/5 text-brand border-brand/20">
                  <Tag className="w-3.5 h-3.5" /> {p.categoria}
                </span>
              )}
              {p.archiviato && (
                <span data-testid={`preventivo-archiviato-${p.numero}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border bg-slate-100 text-slate-600 border-slate-300">
                  <Archive className="w-3.5 h-3.5" /> Archiviato
                </span>
              )}
              {/* Iter122iu · Tag della fase in cui si trovava la pratica alla chiusura,
                  affiancato ad "Archiviato" (es. Sottoscritto / Ricevuto / Incarico accettato). */}
              {p.archiviato && p.ricevuta?.fase_pre_chiusura && p.ricevuta.fase_pre_chiusura !== "Richiesto" && (() => {
                const map = {
                  Sottoscritto: { label: "Sottoscritto", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
                  Ricevuto: { label: "Ricevuto", cls: "bg-sky-100 text-sky-800 border-sky-300" },
                  IncaricoAccettato: { label: "Incarico accettato", cls: "bg-teal-100 text-teal-800 border-teal-300" },
                };
                const m = map[p.ricevuta.fase_pre_chiusura];
                if (!m) return null;
                const d = p.ricevuta.fase_data ? new Date(p.ricevuta.fase_data).toLocaleDateString("it-IT") : null;
                return (
                  <span
                    data-testid={`preventivo-fase-pre-chiusura-${p.numero}`}
                    data-fase={p.ricevuta.fase_pre_chiusura}
                    title={d ? `${m.label} in data ${d}` : m.label}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${m.cls}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> {m.label}{d ? ` · ${d}` : ""}
                  </span>
                );
              })()}
              {/* Iter122fj · Tag "Collegata manutenzione" quando la pratica ha generato un ticket
                  (settato in routes_preventivi::accetta_preventivo). Cliccabile per navigare al ticket. */}
              {p.ticket_id && (
                <button data-testid={`preventivo-ticket-link-${p.numero}`}
                  onClick={(e) => { e.stopPropagation(); navigate(`/segnalazioni/${p.ticket_id}`); }}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200 transition-colors"
                  title="Vai alla manutenzione collegata">
                  <Wrench className="w-3.5 h-3.5" /> Collegata manutenzione{p.ticket_numero ? ` n. ${p.ticket_numero}` : ""}
                </button>
              )}
              {p.data_scadenza && (
                <span data-testid={`preventivo-scadenza-${p.numero}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${
                    p.in_ritardo ? "bg-red-100 text-red-800 border-red-300"
                    : p.in_scadenza ? "bg-amber-100 text-amber-800 border-amber-300"
                    : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                  <CalendarClock className="w-3.5 h-3.5" /> {(p.in_ritardo || p.in_scadenza) ? p.scad_msg : `Scad. ${fmt(p.data_scadenza)}`}
                </span>
              )}
              {p.data_scadenza && ["Richiesto", "Inviato"].includes(p.stato) && !p.archiviato && (
                <ScadenzaBadge data_scadenza={p.data_scadenza} testid={`preventivo-countdown-${p.numero}`} />
              )}
            </div>
          </SharedCard>
          );
        })}
      </div>
      </>)}
      <AnalisiAIPreventiviModal
        open={showAnalisiAI}
        onClose={() => setShowAnalisiAI(false)}
        preventivi={list}
      />
    </div>
  );
}

